/**
 * OkBookModule.jsx — Audiobook library with integrated player
 * Sources: LibriVox (20,000+ free public domain titles) + Fish Audio TTS narration
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, Search, Play, Pause, SkipBack, SkipForward,
  Volume2, Heart, Clock, ChevronLeft, ChevronRight,
  Mic, FileText, X, Loader2, List, Star, BookMarked,
  Rewind, FastForward, Headphones, Globe, ArrowLeft,
  RefreshCw, AlertCircle, Upload
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Genre catalogue (labels come from t() at render time) ──────
const GENRES = [
  { id: "adventure", labelKey: "genre_adventure", emoji: "⚔️", query: "adventure" },
  { id: "classics",  labelKey: "genre_classics",  emoji: "📜", query: "literature" },
  { id: "mystery",   labelKey: "genre_mystery",   emoji: "🔍", query: "mystery" },
  { id: "science",   labelKey: "genre_science",   emoji: "🔬", query: "science" },
  { id: "history",   labelKey: "genre_history",   emoji: "🏛️", query: "history" },
  { id: "fantasy",   labelKey: "genre_fantasy",   emoji: "🐉", query: "fantasy" },
  { id: "romance",   labelKey: "genre_romance",   emoji: "💕", query: "romance" },
  { id: "horror",    labelKey: "genre_horror",    emoji: "👻", query: "horror" },
  { id: "philosophy",labelKey: "genre_philosophy",emoji: "🤔", query: "philosophy" },
  { id: "biography", labelKey: "genre_biography", emoji: "👤", query: "biography" },
  { id: "children",  labelKey: "genre_children",  emoji: "🧸", query: "children" },
  { id: "poetry",    labelKey: "genre_poetry",    emoji: "✍️", query: "poetry" },
];

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 1.75, 2];

// ── Storage helpers ────────────────────────────────────────────
const STORAGE_KEY_FAV = "okbook_favorites";
const STORAGE_KEY_PROGRESS = "okbook_progress";
const STORAGE_KEY_CONTINUE = "okbook_continue";

const loadFavorites = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY_FAV) || "[]"); } catch { return []; }
};
const saveFavorites = (list) => {
  try { localStorage.setItem(STORAGE_KEY_FAV, JSON.stringify(list)); } catch {}
};
const loadProgress = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY_PROGRESS) || "{}"); } catch { return {}; }
};
const saveProgress = (bookId, sectionId, time) => {
  try {
    const p = loadProgress();
    p[`${bookId}_${sectionId}`] = time;
    localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(p));
  } catch {}
};
const loadContinue = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY_CONTINUE) || "[]"); } catch { return []; }
};
const saveContinue = (entry) => {
  try {
    let list = loadContinue();
    list = list.filter(e => e.bookId !== entry.bookId);
    list.unshift(entry);
    if (list.length > 10) list = list.slice(0, 10);
    localStorage.setItem(STORAGE_KEY_CONTINUE, JSON.stringify(list));
  } catch {}
};

// ── Cover image with fallback ──────────────────────────────────
const BookCover = ({ src, title, className = "" }) => {
  const [errored, setErrored] = useState(false);
  const seed = title ? title.charCodeAt(0) % 100 : 42;
  return (
    <img
      src={errored || !src ? `https://picsum.photos/seed/${seed}/200/300` : src}
      alt={title}
      className={className}
      onError={() => setErrored(true)}
    />
  );
};

// ── Main component ─────────────────────────────────────────────
export default function OkBookModule({ user }) {
  const { t } = useLanguage();
  const [view, setView] = useState("home"); // home | search | book | favorites | narrate
  const [query, setQuery] = useState("");
  const [books, setBooks] = useState([]);
  const [newBooks, setNewBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [favorites, setFavorites] = useState(loadFavorites);
  const [continueList, setContinueList] = useState(loadContinue);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genreBooks, setGenreBooks] = useState([]);
  const [genreLoading, setGenreLoading] = useState(false);

  // Player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const progressSaveRef = useRef(null);

  // Narrate
  const [narrateText, setNarrateText] = useState("");
  const [narrateLoading, setNarrateLoading] = useState(false);
  const [narrateAudio, setNarrateAudio] = useState(null);

  // ── Load new books on mount ───────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/okbook/new?limit=12`)
      .then(r => { if (r.data.success) setNewBooks(r.data.books); })
      .catch(() => {});
  }, []);

  // ── Search ────────────────────────────────────────────────────
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setView("search");
    try {
      const r = await axios.get(`${API}/okbook/search?q=${encodeURIComponent(query)}&limit=24`);
      if (r.data.success) setBooks(r.data.books);
      else toast.error("No se pudo buscar. Intenta de nuevo.");
    } catch {
      toast.error("Error al buscar audiolibros.");
    } finally {
      setLoading(false);
    }
  };

  // ── Genre ─────────────────────────────────────────────────────
  const loadGenre = async (genre) => {
    setSelectedGenre(genre);
    setGenreLoading(true);
    setView("search");
    try {
      const r = await axios.get(`${API}/okbook/genre/${encodeURIComponent(genre.query)}?limit=24`);
      if (r.data.success) setGenreBooks(r.data.books);
    } catch {
      toast.error("Error al cargar género.");
    } finally {
      setGenreLoading(false);
    }
  };

  // ── Book detail ───────────────────────────────────────────────
  const openBook = async (book) => {
    if (book.sections && book.sections.length > 0) {
      setSelectedBook(book);
      setView("book");
      return;
    }
    // Fetch full detail
    try {
      const r = await axios.get(`${API}/okbook/book/${book.id}`);
      if (r.data.success && r.data.book) {
        setSelectedBook(r.data.book);
        setView("book");
      }
    } catch {
      setSelectedBook(book);
      setView("book");
    }
  };

  // ── Player controls ───────────────────────────────────────────
  const playSection = useCallback((section, book) => {
    if (!section?.url) { toast.error("Sección sin URL de audio."); return; }

    const audio = audioRef.current;
    if (!audio) return;

    const progress = loadProgress();
    const savedTime = progress[`${book?.id}_${section?.id}`] || 0;

    if (activeSection?.id === section.id && activeSection?.bookId === book?.id) {
      // Toggle play/pause same section
      if (isPlaying) { audio.pause(); setIsPlaying(false); }
      else { audio.play(); setIsPlaying(true); }
      return;
    }

    audio.src = section.url;
    audio.playbackRate = speed;
    audio.volume = volume;
    setActiveSection({ ...section, bookId: book?.id });
    setCurrentTime(0);
    setDuration(0);

    audio.onloadedmetadata = () => {
      setDuration(audio.duration);
      if (savedTime > 0 && savedTime < audio.duration - 5) {
        audio.currentTime = savedTime;
        setCurrentTime(savedTime);
      }
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    };

    // Track progress
    if (book) {
      saveContinue({ bookId: book.id, book, sectionId: section.id, section, timestamp: Date.now() });
      setContinueList(loadContinue());
    }
  }, [activeSection, isPlaying, speed, volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      setCurrentTime(audio.currentTime);
      // Save progress every 5s
      if (!progressSaveRef.current) {
        progressSaveRef.current = setTimeout(() => {
          if (activeSection) saveProgress(activeSection.bookId, activeSection.id, audio.currentTime);
          progressSaveRef.current = null;
        }, 5000);
      }
    };
    const onEnded = () => setIsPlaying(false);
    const onDuration = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("durationchange", onDuration);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("durationchange", onDuration);
      clearTimeout(progressSaveRef.current);
    };
  }, [activeSection]);

  const seek = (val) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = val;
    setCurrentTime(val);
  };

  const changeSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(speed);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const skipSeconds = (s) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + s));
  };

  // ── Favorites ─────────────────────────────────────────────────
  const toggleFav = (book) => {
    let updated;
    if (favorites.some(f => f.id === book.id)) {
      updated = favorites.filter(f => f.id !== book.id);
      toast("Eliminado de favoritos");
    } else {
      updated = [book, ...favorites];
      toast.success("Añadido a favoritos ❤️");
    }
    setFavorites(updated);
    saveFavorites(updated);
  };
  const isFav = (id) => favorites.some(f => f.id === id);

  // ── Narrate ───────────────────────────────────────────────────
  const handleNarrate = async () => {
    if (!narrateText.trim()) { toast.error("Escribe o pega un texto para narrar."); return; }
    setNarrateLoading(true);
    setNarrateAudio(null);
    try {
      const r = await axios.post(`${API}/okbook/narrate`, { text: narrateText, language: "auto" });
      if (r.data.success) {
        setNarrateAudio(r.data.audio_url);
        toast.success("Audio generado ✓");
      } else {
        toast.error(r.data.error || "Error al narrar texto.");
      }
    } catch {
      toast.error("Error al conectar con el servicio de narración.");
    } finally {
      setNarrateLoading(false);
    }
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // ── Displayed books ───────────────────────────────────────────
  const displayedBooks = selectedGenre ? genreBooks : books;
  const displayedLoading = selectedGenre ? genreLoading : loading;

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="h-full flex flex-col bg-void text-white overflow-hidden">
      <audio ref={audioRef} preload="auto" />

      {/* ── Header ── */}
      <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3 flex-shrink-0">
        {view !== "home" && (
          <button
            onClick={() => { setView("home"); setSelectedBook(null); setSelectedGenre(null); }}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <div className="flex items-center gap-2">
          <BookOpen size={22} className="text-violet-400" />
          <h1 className="text-xl font-bold">OkBook</h1>
          <span className="text-xs bg-violet-500/20 text-violet-300 px-2 py-0.5 rounded-full">
            {t('okbookSource')}
          </span>
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => setView("narrate")}
            className={`p-2 rounded-lg transition-colors ${view === "narrate" ? "bg-violet-500/30 text-violet-300" : "bg-white/5 hover:bg-white/10"}`}
            title={t('narrateWithAI')}
          >
            <Mic size={18} />
          </button>
          <button
            onClick={() => setView("favorites")}
            className={`p-2 rounded-lg transition-colors ${view === "favorites" ? "bg-violet-500/30 text-violet-300" : "bg-white/5 hover:bg-white/10"}`}
            title={t('myFavorites')}
          >
            <Heart size={18} />
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">

        {/* ════ HOME ════ */}
        {view === "home" && (
          <div className="p-6 space-y-8">
            {/* Search bar */}
            <form onSubmit={handleSearch} className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('searchBooks')}
                className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm outline-none focus:border-violet-500/50 transition-colors"
              />
              {query && (
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-violet-500 hover:bg-violet-600 rounded-lg text-xs font-medium transition-colors">
                  {t('search')}
                </button>
              )}
            </form>

            {/* Continue listening */}
            {continueList.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Clock size={14} /> {t('continueListening')}
                </h2>
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                  {continueList.map(entry => (
                    <button
                      key={entry.bookId}
                      onClick={() => openBook(entry.book)}
                      className="flex-shrink-0 w-32 text-left group"
                    >
                      <div className="relative mb-2">
                        <BookCover
                          src={entry.book?.cover}
                          title={entry.book?.title}
                          className="w-32 h-44 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Play size={24} className="text-white" />
                        </div>
                      </div>
                      <p className="text-xs font-medium truncate">{entry.book?.title}</p>
                      <p className="text-xs text-gray-500 truncate">{entry.book?.author}</p>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Genre carousel */}
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {t('exploreGenres')}
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {GENRES.map(g => (
                  <button
                    key={g.id}
                    onClick={() => loadGenre(g)}
                    className="flex flex-col items-center gap-2 p-4 bg-white/5 hover:bg-violet-500/20 border border-white/5 hover:border-violet-500/30 rounded-xl transition-all"
                  >
                    <span className="text-2xl">{g.emoji}</span>
                    <span className="text-xs text-center font-medium">{t(g.labelKey)}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* New releases */}
            {newBooks.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <BookOpen size={14} /> {t('newArrivals')}
                </h2>
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                  {newBooks.map(book => (
                    <BookCard key={book.id} book={book} onOpen={openBook} isFav={isFav(book.id)} onFav={toggleFav} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* ════ SEARCH / GENRE RESULTS ════ */}
        {view === "search" && (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-5">
              {selectedGenre && (
                <span className="text-2xl">{selectedGenre.emoji}</span>
              )}
              <h2 className="text-lg font-semibold">
                {selectedGenre ? t(selectedGenre.labelKey) : `${t('resultsFor')} "${query}"`}
              </h2>
            </div>

            {/* Search within results */}
            {!selectedGenre && (
              <form onSubmit={handleSearch} className="relative mb-5">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('newSearch')}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm outline-none focus:border-violet-500/50"
                />
              </form>
            )}

            {displayedLoading ? (
              <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
                <Loader2 size={24} className="animate-spin" />
                <span>{t('searchingBooks')}</span>
              </div>
            ) : displayedBooks.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <AlertCircle size={40} className="mx-auto mb-3 opacity-40" />
                <p>{t('noResults')}</p>
                <p className="text-xs mt-1">{t('noResultsHint')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {displayedBooks.map(book => (
                  <BookCard key={book.id} book={book} onOpen={openBook} isFav={isFav(book.id)} onFav={toggleFav} vertical />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════ BOOK DETAIL ════ */}
        {view === "book" && selectedBook && (
          <BookDetail
            book={selectedBook}
            isFav={isFav(selectedBook.id)}
            onFav={toggleFav}
            activeSection={activeSection}
            isPlaying={isPlaying}
            onPlay={playSection}
          />
        )}

        {/* ════ FAVORITES ════ */}
        {view === "favorites" && (
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-5 flex items-center gap-2">
              <Heart size={18} className="text-red-400" /> {t('myFavorites')}
            </h2>
            {favorites.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Heart size={40} className="mx-auto mb-3 opacity-30" />
                <p>{t('noFavorites')}</p>
                <p className="text-xs mt-1">{t('noFavoritesHint')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {favorites.map(book => (
                  <BookCard key={book.id} book={book} onOpen={openBook} isFav={true} onFav={toggleFav} vertical />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════ NARRATE TEXT ════ */}
        {view === "narrate" && (
          <div className="p-6 max-w-2xl mx-auto">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Mic size={18} className="text-violet-400" /> {t('narrateWithAI')}
            </h2>
            <p className="text-sm text-gray-400 mb-5">
              {t('narrateHint')}
            </p>

            <textarea
              value={narrateText}
              onChange={e => setNarrateText(e.target.value)}
              placeholder={t('narrateHint')}
              rows={10}
              className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm resize-none outline-none focus:border-violet-500/50 transition-colors mb-3"
              maxLength={4000}
            />
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-gray-500">{narrateText.length}/4000 {t('charsMax')}</span>
              <button
                onClick={() => setNarrateText("")}
                className="text-xs text-gray-500 hover:text-gray-300"
              >
                {t('clear')}
              </button>
            </div>

            <button
              onClick={handleNarrate}
              disabled={narrateLoading || !narrateText.trim()}
              className="w-full py-3 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {narrateLoading ? (
                <><Loader2 size={18} className="animate-spin" /> {t('generatingAudio')}</>
              ) : (
                <><Headphones size={18} /> {t('generateNarration')}</>
              )}
            </button>

            {narrateAudio && (
              <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-sm text-gray-300 mb-3 flex items-center gap-2">
                  <Volume2 size={16} className="text-violet-400" /> {t('readyAudio')}:
                </p>
                <audio controls src={narrateAudio} className="w-full" />
                <a
                  href={narrateAudio}
                  download="okbook_narration.mp3"
                  className="mt-3 inline-flex items-center gap-2 text-xs text-violet-400 hover:text-violet-300"
                >
                  {t('downloadMp3')}
                </a>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── Persistent mini player ── */}
      {activeSection && (
        <MiniPlayer
          section={activeSection}
          book={selectedBook}
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          speed={speed}
          onPlayPause={() => {
            const audio = audioRef.current;
            if (!audio) return;
            if (isPlaying) { audio.pause(); setIsPlaying(false); }
            else { audio.play(); setIsPlaying(true); }
          }}
          onSeek={seek}
          onSkip={skipSeconds}
          onSpeedChange={changeSpeed}
          onClose={() => {
            audioRef.current?.pause();
            setIsPlaying(false);
            setActiveSection(null);
          }}
          formatTime={formatTime}
        />
      )}
    </div>
  );
}

// ── BookCard ───────────────────────────────────────────────────
function BookCard({ book, onOpen, isFav, onFav, vertical = false }) {
  return (
    <div className={`flex-shrink-0 ${vertical ? "w-full" : "w-36"} group relative`}>
      <button className="w-full text-left" onClick={() => onOpen(book)}>
        <div className="relative mb-2 overflow-hidden rounded-lg">
          <BookCover
            src={book.cover}
            title={book.title}
            className={`w-full ${vertical ? "h-52" : "h-48"} object-cover`}
          />
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="bg-violet-500 rounded-full p-2">
              <Play size={18} className="text-white" />
            </div>
          </div>
        </div>
        <p className="text-xs font-semibold truncate leading-tight">{book.title}</p>
        <p className="text-xs text-gray-500 truncate">{book.author}</p>
        {book.language && (
          <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
            <Globe size={10} /> {book.language}
          </p>
        )}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onFav(book); }}
        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Heart size={12} className={isFav ? "fill-red-400 text-red-400" : "text-white"} />
      </button>
    </div>
  );
}

// ── BookDetail ─────────────────────────────────────────────────
function BookDetail({ book, isFav, onFav, activeSection, isPlaying, onPlay }) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Book header */}
      <div className="flex gap-5 mb-6">
        <div className="flex-shrink-0">
          <BookCover
            src={book.cover}
            title={book.title}
            className="w-32 h-44 object-cover rounded-xl shadow-lg"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold mb-1 leading-tight">{book.title}</h2>
          <p className="text-gray-400 mb-2">{book.author}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {book.language && (
              <span className="text-xs bg-white/10 rounded-full px-2 py-0.5 flex items-center gap-1">
                <Globe size={10} /> {book.language}
              </span>
            )}
            {book.total_time && (
              <span className="text-xs bg-white/10 rounded-full px-2 py-0.5 flex items-center gap-1">
                <Clock size={10} /> {book.total_time}
              </span>
            )}
            {book.genres?.slice(0, 2).map(g => (
              <span key={g} className="text-xs bg-violet-500/20 text-violet-300 rounded-full px-2 py-0.5">{g}</span>
            ))}
          </div>
          <button
            onClick={() => onFav(book)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isFav ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-white/5 hover:bg-white/10 border border-white/10"
            }`}
          >
            <Heart size={14} className={isFav ? "fill-red-400" : ""} />
            {isFav ? t('removeFromFavorites') : t('addToFavorites')}
          </button>
        </div>
      </div>

      {/* Description */}
      {book.description && (
        <div className="mb-6">
          <p className={`text-sm text-gray-300 leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
            {book.description}
          </p>
          {book.description.length > 200 && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-violet-400 hover:text-violet-300 mt-1">
              {expanded ? t('viewLess') : t('viewMore')}
            </button>
          )}
        </div>
      )}

      {/* Chapters / Sections */}
      <div>
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <List size={14} /> {t('chapters')} ({book.sections?.length || book.num_sections || 0})
        </h3>

        {(!book.sections || book.sections.length === 0) ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <AlertCircle size={24} className="mx-auto mb-2 opacity-40" />
            <p>{t('noChapters')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {book.sections.map((section, idx) => {
              const isActive = activeSection?.id === section.id && activeSection?.bookId === book.id;
              return (
                <button
                  key={section.id || idx}
                  onClick={() => onPlay(section, book)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                    isActive
                      ? "bg-violet-500/20 border border-violet-500/30"
                      : "bg-white/3 hover:bg-white/8 border border-transparent"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isActive ? "bg-violet-500" : "bg-white/10"
                  }`}>
                    {isActive && isPlaying ? <Pause size={14} /> : <Play size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {section.chapter_number != null ? `${section.chapter_number}. ` : ""}
                      {section.title || `Capítulo ${idx + 1}`}
                    </p>
                    {section.reader && (
                      <p className="text-xs text-gray-500 truncate">{t('narrator')}: {section.reader}</p>
                    )}
                  </div>
                  {section.duration && (
                    <span className="text-xs text-gray-500 flex-shrink-0">{section.duration}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MiniPlayer ─────────────────────────────────────────────────
function MiniPlayer({ section, book, isPlaying, currentTime, duration, speed, onPlayPause, onSeek, onSkip, onSpeedChange, onClose, formatTime }) {
  const { t } = useLanguage();
  return (
    <div className="flex-shrink-0 border-t border-white/5 bg-surface px-4 py-3">
      <div className="max-w-3xl mx-auto">
        {/* Track info */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{section?.title || t('nowPlaying')}</p>
            <p className="text-xs text-gray-500 truncate">{book?.title || ""}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-300 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={currentTime}
          onChange={e => onSeek(Number(e.target.value))}
          className="w-full h-1 mb-2 accent-violet-400 cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500 mb-2">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => onSkip(-30)} className="p-1 text-gray-400 hover:text-white transition-colors" title={t('rewind30')}>
            <Rewind size={18} />
          </button>
          <button
            onClick={onPlayPause}
            className="w-10 h-10 rounded-full bg-violet-500 hover:bg-violet-600 flex items-center justify-center transition-colors"
          >
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>
          <button onClick={() => onSkip(30)} className="p-1 text-gray-400 hover:text-white transition-colors" title={t('forward30')}>
            <FastForward size={18} />
          </button>
          <button
            onClick={onSpeedChange}
            className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors font-mono min-w-[44px] text-center"
            title={t('playbackSpeed')}
          >
            {speed}x
          </button>
        </div>
      </div>
    </div>
  );
}
